import { create } from 'zustand';
import {
  WorkOrder, InventoryItem, MachineState, Alert, MESEvent,
  initialWorkOrders, initialInventory, initialMachines, initialAlerts, initialEvents,
} from '@/data/mockData';
import { createAlert as mesCreateAlert, createEvent as mesCreateEvent } from '@/integrations/api/mes_api';

export interface UIState {
  dispatchBoard: {
    priorityFilter: 'ALL' | 'URGENT' | 'NORMAL' | 'LOW';
    searchTerm: string;
  };
  qualityBoard: {
    severityFilter: 'ALL' | 'CRITICAL' | 'MAJOR' | 'MINOR';
    dispositionFilter: 'ALL' | 'PENDING' | 'REWORK' | 'SCRAP' | 'RELEASE';
  };
  wipBoard: {
    stationFilter: string | null;
  };
}

interface MESState {
  workOrders: WorkOrder[];
  inventory: InventoryItem[];
  machines: MachineState[];
  alerts: Alert[];
  events: MESEvent[];
  activeOrderId: string;
  activeStation: string | null;
  activePlant: string | null;
  activeLine: string | null;
  activeOperation: string;
  activeSerial: string;
  uiState: UIState;

  // Actions
  updateMachineState: (id: string, status: MachineState['status']) => void;
  addAlert: (alert: Omit<Alert, 'id'>) => void;
  acknowledgeAlert: (id: string) => void;
  updateWOProgress: (id: string, produced: number, defects?: number) => void;
  addEvent: (event: Omit<MESEvent, 'id'>) => void;
  issueInventory: (sku: string, qty: number) => void;
  setActiveOrder: (orderId: string) => void;
  setActivePlant: (plantId: string | null) => void;
  setActiveLine: (lineId: string | null) => void;
  setActiveStation: (stationId: string | null) => void;
  setDispatchFilter: (filter: Partial<UIState['dispatchBoard']>) => void;
  setQualityFilter: (filter: Partial<UIState['qualityBoard']>) => void;
  setWIPFilter: (filter: Partial<UIState['wipBoard']>) => void;
  resetFilters: (view?: 'dispatch' | 'quality' | 'wip') => void;
}

const defaultUIState: UIState = {
  dispatchBoard: { priorityFilter: 'ALL', searchTerm: '' },
  qualityBoard: { severityFilter: 'ALL', dispositionFilter: 'ALL' },
  wipBoard: { stationFilter: null },
};

export const useMESStore = create<MESState>((set) => ({
  workOrders: initialWorkOrders,
  inventory: initialInventory,
  machines: initialMachines,
  alerts: initialAlerts,
  events: initialEvents,
  activeOrderId: 'WO-2024-0892',
  activeStation: null,
  activePlant: null,
  activeLine: null,
  activeOperation: 'OP-030',
  activeSerial: 'SN-78234',
  uiState: defaultUIState,

  setDispatchFilter: (filter) =>
    set((s) => ({ uiState: { ...s.uiState, dispatchBoard: { ...s.uiState.dispatchBoard, ...filter } } })),
  setQualityFilter: (filter) =>
    set((s) => ({ uiState: { ...s.uiState, qualityBoard: { ...s.uiState.qualityBoard, ...filter } } })),
  setWIPFilter: (filter) =>
    set((s) => ({ uiState: { ...s.uiState, wipBoard: { ...s.uiState.wipBoard, ...filter } } })),
  resetFilters: (view) =>
    set((s) => {
      if (!view) return { uiState: defaultUIState };
      if (view === 'dispatch') return { uiState: { ...s.uiState, dispatchBoard: defaultUIState.dispatchBoard } };
      if (view === 'quality') return { uiState: { ...s.uiState, qualityBoard: defaultUIState.qualityBoard } };
      return { uiState: { ...s.uiState, wipBoard: defaultUIState.wipBoard } };
    }),

  updateMachineState: (id, status) =>
    set((s) => ({
      machines: s.machines.map((m) => m.id === id ? { ...m, status } : m),
    })),

  addAlert: (alert) => {
    const id = `ALT-${Date.now()}`;
    set((s) => ({
      alerts: [{ ...alert, id }, ...s.alerts],
    }));
    // Persist via MES API (fire-and-forget)
    mesCreateAlert({
      severity: alert.severity,
      message: alert.message,
      source: alert.source,
      acknowledged: alert.acknowledged,
    }).catch((e) => console.error('Failed to persist alert:', e?.message ?? e));
  },

  acknowledgeAlert: (id) =>
    set((s) => ({
      alerts: s.alerts.map((a) => a.id === id ? { ...a, acknowledged: true } : a),
    })),

  updateWOProgress: (id, produced, defects = 0) =>
    set((s) => ({
      workOrders: s.workOrders.map((wo) => {
        if (wo.id !== id) return wo;
        const newProduced = wo.producedQty + produced;
        const newDefects = wo.defectQty + defects;
        const progress = Math.min(100, Math.round((newProduced / wo.plannedQty) * 100));
        return { ...wo, producedQty: newProduced, defectQty: newDefects, progress, status: progress >= 100 ? 'COMPLETED' : wo.status };
      }),
    })),

  addEvent: (event) => {
    const id = `EVT-${Date.now()}`;
    set((s) => ({
      events: [{ ...event, id }, ...s.events].slice(0, 50),
    }));
    // Persist via MES API (fire-and-forget)
    mesCreateEvent({
      type: event.type,
      station: event.station,
      user_name: event.user,
      status: event.status,
      detail: event.detail,
    }).catch((e) => console.error('Failed to persist event:', e?.message ?? e));
  },

  issueInventory: (sku, qty) =>
    set((s) => ({
      inventory: s.inventory.map((item) =>
        item.sku === sku ? { ...item, physicalQty: Math.max(0, item.physicalQty - qty) } : item
      ),
    })),

  setActiveOrder: (orderId) => set({ activeOrderId: orderId }),
  setActivePlant: (plantId) => set({ activePlant: plantId, activeLine: null, activeStation: null }),
  setActiveLine: (lineId) => set({ activeLine: lineId, activeStation: null }),
  setActiveStation: (stationId) => set({ activeStation: stationId }),
}));
